import styled, { css } from "styled-components";

type HeaderVariant = "default" | "transparent" | "primary" | "surface";

export const Container = styled.header<{
  $hidden: boolean;
  $variant: HeaderVariant;
  $needsSpacer: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  height: 60px;
  transition:
    transform 0.3s ease,
    background-color 0.3s ease,
    box-shadow 0.3s ease;
  transform: ${({ $hidden }) =>
    $hidden ? "translateY(-100%)" : "translateY(0)"};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  ${({ $variant }) => {
    if ($variant === "transparent") {
      return css`
        background: transparent;
        box-shadow: none;
      `;
    }
    if ($variant === "primary") {
      return css`
        background: var(--color-primary);
      `;
    }
    return css`
      background: var(--color-surface);
    `;
  }}
`;

export const Spacer = styled.div`
  height: 60px;
  flex-shrink: 0;
`;

export const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  width: 100%;
  padding: 0.5rem 2rem;

  @media (min-width: 768px) {
    padding: 0.5rem 5rem;
  }
`;

export const LogoButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  img {
    height: 40px;
    object-fit: contain;

    @media (min-width: 1024px) {
      height: 40px;
    }
  }
`;

export const MenuWrap = styled.div`
  position: relative;

  @media (min-width: 1024px) {
    &:hover .desktop-dropdown,
    &:focus-within .desktop-dropdown {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
      pointer-events: auto;
    }
  }
`;

export const MenuToggle = styled.button<{ $light: boolean }>`
  border: none;
  background: transparent;
  padding: 0.35rem;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: ${({ $light }) => ($light ? '#fff' : 'var(--color-primary)')};
  border-radius: 0.5rem;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ $light }) =>
      $light ? 'rgba(255,255,255,0.12)' : 'rgba(14,34,68,0.06)'};
  }

  svg {
    width: 26px;
    height: 26px;
  }
`;

export const DesktopDropdown = styled.div`
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    position: absolute;
    top: 100%;
    right: 0;
    min-width: 260px;
    flex-direction: column;
    gap: 0.15rem;
    background: transparent;
    padding: 0.75rem 0 0;
    z-index: 60;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-6px);
    pointer-events: none;
    transition:
      opacity 0.18s ease,
      transform 0.18s ease,
      visibility 0.18s ease;

    .dropdown-panel {
      background: #fff;
      border-radius: 1rem;
      box-shadow: 0 16px 40px rgba(14, 34, 68, 0.16);
      padding: 0.65rem;
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
    }
  }
`;

export const MenuItem = styled.button`
  border: none;
  background: transparent;
  text-align: left;
  width: 100%;
  padding: 0.7rem 0.85rem;
  border-radius: 0.75rem;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  transition:
    background 0.2s ease,
    color 0.2s ease;

  svg {
    width: 1.05rem;
    height: 1.05rem;
    flex-shrink: 0;
    opacity: 0.85;
  }

  &:hover {
    background: var(--color-surface);
    color: var(--color-secondary);
  }
`;

export const MenuDivider = styled.div`
  height: 1px;
  background: rgba(14, 34, 68, 0.08);
  margin: 0.4rem 0.35rem;
`;

export const MenuUserBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.85rem;
  background: linear-gradient(
    135deg,
    rgba(14, 34, 68, 0.06) 0%,
    rgba(37, 70, 142, 0.08) 100%
  );
  margin-bottom: 0.25rem;

  img,
  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    color: var(--color-primary);
    flex-shrink: 0;
  }

  .meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .name {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .role {
    font-size: 0.7rem;
    font-weight: 500;
    color: var(--color-secondary);
    opacity: 0.85;
  }
`;

export const MobileOverlay = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 55;

  @media (min-width: 1024px) {
    display: none;
  }
`;

export const MobileDrawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(320px, 86vw);
  background: #fff;
  z-index: 60;
  transform: ${({ $open }) => ($open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease;
  padding: 1.5rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);

  @media (min-width: 1024px) {
    display: none;
  }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;

  h3 {
    font-size: 1rem;
    font-weight: 700;
    color: var(--color-primary);
  }

  button {
    border: none;
    background: transparent;
    color: var(--color-primary);
    cursor: pointer;
    display: grid;
    place-items: center;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

export const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 0.85rem;
  background: linear-gradient(
    135deg,
    rgba(14, 34, 68, 0.06) 0%,
    rgba(37, 70, 142, 0.08) 100%
  );
  margin-bottom: 0.75rem;

  img,
  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    color: var(--color-primary);
  }

  .meta {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .name {
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--color-primary);
  }

  .role {
    font-size: 0.72rem;
    font-weight: 500;
    color: var(--color-secondary);
  }
`;

export const ContentModalGoogle = styled.div`
  padding: 0.9rem 0.5rem;
  text-align: center;
  padding-bottom: 34px;

  .title-logout {
    font-size: 0.9rem;
  }

  .input-login {
    width: 100%;
    height: 2.5rem;
    margin-bottom: 0.5rem;
    border-radius: 5px;
    background-color: var(--background-footer);
    border: none;
    font-size: 0.9rem;
    padding: 0 0 0 0.5rem;
  }

  .input-login-div {
    width: 100%;
    height: 2.5rem;
    margin-bottom: 0.5rem;
    border-radius: 5px;
    background-color: var(--background-footer);
    border: none;
    font-size: 0.9rem;
    padding: 0 0 0 0.5rem;
    display: flex;
    flex-direction: row;
  }

  .input-div-login {
    border: none;
    background-color: transparent;
    width: 80%;
    font-size: 0.9rem;
  }

  .button-eye {
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20%;
    background-color: transparent;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  .button-login {
    width: 100%;
    height: 2.5rem;
    border-radius: 5px;
    border: none;
    font-weight: 600;
    background-color: var(--background-button-component);
    color: var(--bg-color);
    cursor: pointer;
    transition: 0.3s;

    &:hover {
      filter: brightness(0.8);
    }
  }

  .form-group {
    display: block;
    text-align: left;
    margin-bottom: 2px;
  }

  .content-button {
    display: flex;
    justify-content: center;

    button {
      margin: 12px 0 !important;
      width: 100%;
      height: 2.5rem;
      border-radius: 5px;
      border: none;
      font-weight: 600;
      background-color: var(--background-button-component);
      color: var(--bg-color);
      cursor: pointer;
    }
  }

  img {
    max-width: 160px;
    margin-bottom: 1rem;
  }
`;
